"use client";

import { useEffect } from "react";
import { redirectToLogin } from "@/lib/auth-redirect";

/**
 * SessionExpiryWatcher — polls /api/auth/session and redirects to
 * /login when the NextAuth session has expired.
 *
 *   We deliberately do NOT use next-auth/react's SessionProvider +
 *   useSession() here. Earlier versions of this component did, and
 *   the SessionProvider in next-auth v5 beta (with React 19's
 *   streaming SSR) produced spurious "useSourceSlider must be used
 *   inside <SourceSliderProvider>" errors on dashboard pages. The
 *   SessionProvider's internal Suspense-like behavior interfered
 *   with downstream client-component context resolution during the
 *   initial SSR pass — no placement of SessionProvider in the tree
 *   avoided the issue.
 *
 *   This rewrite uses a plain fetch in a useEffect. useEffect never
 *   runs during SSR, so the watcher contributes zero work to the
 *   server-render pass and can't interfere with sibling/descendant
 *   client components.
 *
 *   Watcher behavior:
 *   - Initial check on mount.
 *   - Re-check every 5 minutes.
 *   - Re-check on tab focus (catches sessions that died while the
 *     tab was backgrounded).
 *   - When the session response carries
 *     `error === "RefreshAccessTokenError"` (set by the jwt
 *     callback in lib/auth.ts on refresh failure), redirect to
 *     /login with the current path as callbackUrl.
 *   - Additionally pings /api/auth/heartbeat which proxies to
 *     raven-service's single-active-session check. A 401 there
 *     means a newer browser logged in as the same user and this
 *     tab has been superseded — also redirect to /login. Without
 *     this, an idle tab on Browser A wouldn't know it had been
 *     kicked by a login on Browser B until the user clicked
 *     something.
 *
 *   This is the "no XHR currently in flight" recovery path — the
 *   api.ts 401 interceptor handles the "user just tried to fetch
 *   something" case symmetrically.
 *
 *   Renders null; side-effect-only component.
 */
export function SessionExpiryWatcher() {
  useEffect(() => {
    let cancelled = false;

    const checkSession = async () => {
      try {
        const res = await fetch("/api/auth/session", { cache: "no-store" });
        if (cancelled || !res.ok) return;
        const session = await res.json();
        if (cancelled) return;
        if (session?.error === "RefreshAccessTokenError") {
          redirectToLogin("session_expired");
        }
      } catch {
        // Transient network failure — next interval will retry. Don't
        // redirect on fetch errors, only on confirmed RefreshAccessTokenError.
      }
    };

    const checkHeartbeat = async () => {
      try {
        const res = await fetch("/api/auth/heartbeat", { cache: "no-store" });
        if (cancelled) return;
        // 401 → either NextAuth session is gone OR raven-service
        // rejected the session id (superseded by another browser).
        // Either way, this tab is no longer authorized — redirect.
        // 503 is the proxy's "transient error" signal; we ignore it
        // and try again on the next tick.
        if (res.status === 401) {
          redirectToLogin("session_expired");
        }
      } catch {
        // Same as above — transient errors must not log the user out.
      }
    };

    const tick = () => {
      checkSession();
      checkHeartbeat();
    };

    tick();
    const interval = setInterval(tick, 5 * 60 * 1000);
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return null;
}
